import { useId, useLayoutEffect, useRef, useState } from "react";
import { rrect, type Pip } from "@shared/motion";

/** Высота хвостика и его ширина вдоль края — `PH` и `pw` из `tooltip` набора v6. */
export const TIP_PIP = 7;
export const TIP_ALONG = 16;
/** Сбоку хвостик уже: ему нужен прямой участок края, а подсказка низкая. */
const PIP_ACROSS = 12;
/** Выше этого — подсказка в несколько строк. */
const MULTI = 34;

const OPPOSITE = { top: "bottom", bottom: "top", left: "right", right: "left" } as const;

/**
 * Форма подсказки — одна фигура с хвостиком, как в наборе v6: хвостик вписан в
 * контур плавными изгибами от края (`rrect` из `shared/motion`), а не приставлен
 * треугольником. По этой фигуре вырезан материал «Почты», идёт кромка и
 * ложится тень — у хвостика они те же, что у всей подсказки.
 *
 * Куда смотреть хвостику, говорит стрелка Radix (спрятана): она уже стоит
 * против середины кнопки и сдвигается, когда подсказку поджимает край
 * экрана. Однострочная подсказка — капсула, многострочная — плашка со
 * скруглением 14.
 *
 * Слой шире подсказки на высоту хвостика с каждой стороны: материалу нужна
 * коробка, в которую хвостик помещается.
 */
export function TipShape() {
  const ref = useRef<HTMLSpanElement>(null);
  const [shape, setShape] = useState<{ W: number; H: number; d: string } | null>(null);
  const id = useId().replace(/[^\w-]/g, "");

  useLayoutEffect(() => {
    const box = ref.current!.parentElement!;
    let frame = 0;
    let tries = 0;
    let key = "";

    /*
     * Место хвостика вдоль края. Radix ставит стрелку по оси её длины: вдоль
     * верхнего края это ширина (16), вдоль бокового — высота (7). Хвостик не
     * заходит на скругления; некуда — стоит посередине.
     */
    const pip = (radius: number, W: number, H: number): Pip | null => {
      const side = box.dataset.side as keyof typeof OPPOSITE | undefined;
      const arrow = box.querySelector<SVGElement>(".lui-tip-arrow")?.parentElement;
      if (!side || !arrow) return null;
      const hor = side === "top" || side === "bottom";
      const mid = hor ? parseFloat(arrow.style.left) + TIP_ALONG / 2 : parseFloat(arrow.style.top) + TIP_PIP / 2;
      if (!Number.isFinite(mid)) return null;
      const len = hor ? W : H;
      const w = hor ? TIP_ALONG : PIP_ACROSS;
      const lo = radius + w / 2 + 2;
      const hi = len - radius - w / 2 - 2;
      const at = hi < lo ? len / 2 : Math.max(lo, Math.min(hi, mid));
      return { side: OPPOSITE[side], at, w, h: TIP_PIP };
    };

    const measure = () => {
      // Многострочной поля шире (правило `.lui-tip[data-multi]`).
      if (box.offsetHeight > MULTI) box.dataset.multi = "";
      else delete box.dataset.multi;
      const W = box.offsetWidth;
      const H = box.offsetHeight;
      const round = H > MULTI ? 14 : H / 2;
      const side = box.dataset.side;
      const radius = side === "left" || side === "right" ? Math.min(round, Math.floor((H - PIP_ACROSS) / 2) - 1) : round;
      const p = pip(radius, W, H);
      return { W, H, radius, p };
    };

    const place = () => {
      const { W, H, radius, p } = measure();
      const next = `${W}|${H}|${p?.side}|${p?.at.toFixed(1)}`;
      if (next === key) return;
      key = next;
      setShape({ W, H, d: rrect([[TIP_PIP, TIP_PIP + W]], H, TIP_PIP, radius, p) });
    };

    // Стрелку Radix ставит, когда рассчитал положение, — кадром позже монтажа.
    const wait = () => {
      if (measure().p || tries++ >= 3) place();
      else frame = requestAnimationFrame(wait);
    };
    frame = requestAnimationFrame(wait);

    // Подсказку сдвинуло или перевернуло (край экрана, сменилась подпись) — хвостик за ней.
    const mo = new MutationObserver(place);
    mo.observe(box, { attributes: true, attributeFilter: ["data-side"] });
    const arrow = box.querySelector<SVGElement>(".lui-tip-arrow")?.parentElement;
    if (arrow) mo.observe(arrow, { attributes: true, attributeFilter: ["style"] });
    const ro = new ResizeObserver(place);
    ro.observe(box);

    return () => {
      cancelAnimationFrame(frame);
      mo.disconnect();
      ro.disconnect();
    };
  }, []);

  const W = (shape?.W ?? 0) + TIP_PIP * 2;
  const H = (shape?.H ?? 0) + TIP_PIP * 2;
  return (
    <span ref={ref} className="lui-tip-shape" aria-hidden>
      {shape && (
        <>
          <span className="lui-tip-surface" style={{ clipPath: `path("${shape.d}")` }} />
          <svg className="lui-tip-lines" width={W} height={H} viewBox={`0 0 ${W} ${H}`}>
            <defs>
              <clipPath id={`${id}in`}>
                <path d={shape.d} />
              </clipPath>
              {/* Тень — только снаружи фигуры, как у `box-shadow`: под материал она не заходит. */}
              <mask id={`${id}out`} maskUnits="userSpaceOnUse" x={-40} y={-40} width={W + 80} height={H + 80}>
                <rect x={-40} y={-40} width={W + 80} height={H + 80} fill="#fff" />
                <path d={shape.d} fill="#000" />
              </mask>
              <filter id={`${id}blur`} filterUnits="userSpaceOnUse" x={-40} y={-40} width={W + 80} height={H + 80}>
                <feGaussianBlur stdDeviation={8} />
              </filter>
              {/* Кромка — как у материала: светлее сверху, тише книзу (16% → 4%). */}
              <linearGradient id={`${id}edge`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0" stopColor="#fff" stopOpacity={0.16} />
                <stop offset="0.7" stopColor="#fff" stopOpacity={0.04} />
              </linearGradient>
            </defs>
            <g mask={`url(#${id}out)`}>
              <path d={shape.d} transform="translate(0 6)" fill="#000" fillOpacity={0.22} filter={`url(#${id}blur)`} />
            </g>
            {/* Толщина 2 по осевой и обрезка фигурой — линия в 1px внутри, как кромка `surface-pop`. */}
            <path d={shape.d} fill="none" stroke={`url(#${id}edge)`} strokeWidth={2} clipPath={`url(#${id}in)`} />
          </svg>
        </>
      )}
    </span>
  );
}
